import * as React from 'react';
import { Checkmark, Lock, Plus } from '@bloomberg/media-ui-icons';

import { Button } from '../Button/index';
import type { ButtonProps } from '../Button/types';
import { useSlug } from '../hooks/useSlug';
import { classes } from '../utils/index';

import styles from './styles.module.css';

type InactiveIcon = 'plus' | 'lock';
type CurrentDisplayState = {
  icon: React.ReactElement;
  value:
    | 'activeState'
    | 'lockedState'
    | 'unselectedState'
    | 'disabledActiveState';
};

type PhoenixRoundedToggleButtonProps = {
  version?: 'phoenix';
  colorMode?: 'light' | 'dark';
  /** @param {string} size - large is only supported in version=phoenix */
  size?: 'small' | 'default' | 'large';
  activeText?: string;
  inactiveText?: string;
  inactiveIcon?: InactiveIcon;
  onlyIcon?: boolean;
  /** @param { boolean } disableWhenActive - 'toggle' is replaced with a one-way enable, where active disables UI interaction. this is a version=phoenix only prop. */
  disableWhenActive?: boolean;
  onToggle?: React.MouseEventHandler<HTMLButtonElement>;
};

export type RoundedToggleButtonProps = Omit<ButtonProps, 'size'> &
  PhoenixRoundedToggleButtonProps & {
    active?: boolean;
    activeText?: string;
    inactiveText?: string;
    /** @param {string} colorMode - usable in version=phoenix only*/
    colorMode?: 'light' | 'dark';
    disabled?: boolean;
    withIcon?: boolean;
    onlyIcon?: boolean;
    inactiveIcon?: InactiveIcon;
    /** @deprecated in version=phoenix, use colorMode=light|dark instead */
    dark?: boolean;
    onToggle?: React.MouseEventHandler<HTMLButtonElement>;
    buttonProps?: ButtonProps;
    activeHoverText?: string;
    title?: string;
  };

const RoundedToggleButton = React.forwardRef<
  HTMLButtonElement,
  RoundedToggleButtonProps
>(function Component(props, ref) {
  const {
    name = 'RoundedToggleButton',
    colorMode = 'light',
    size = 'default',
    inactiveIcon = 'plus',
    activeHoverText = '',
    withIcon = false,
    onlyIcon = false,
    dark = false,
    disableWhenActive = false,
    title = '',
    version = undefined,
    activeText,
    inactiveText,
    active,
    onToggle,
    disabled,
    buttonProps,
  } = props as RoundedToggleButtonProps;

  const [componentName] = useSlug(name);

  const currentState: CurrentDisplayState = React.useMemo(() => {
    if (active && !disabled) {
      if (disableWhenActive) {
        return {
          icon: <Checkmark className={styles.icon} title={title} />,
          value: 'disabledActiveState',
        };
      }

      return {
        icon: <Checkmark className={styles.icon} title={title} />,
        value: 'activeState',
      };
    }

    const _inactiveIcon =
      inactiveIcon === 'lock' ? (
        <Lock className={styles.icon} title={title} />
      ) : (
        <Plus className={styles.icon} title={title} />
      );

    if (disabled) {
      if (version === 'phoenix') {
        return {
          icon: <Lock className={styles.icon} title={title} />,
          value: 'lockedState',
        };
      }

      // sundae RoundedToggleButton had state where it could be disabled
      // and active and show the `props.inactiveIcon`
      // So for compatibility, when version=undefined, allow that to
      // keep happening.

      // Phoenix however defines disabled as always using the lock

      return { icon: _inactiveIcon, value: 'lockedState' };
    }

    return { icon: _inactiveIcon, value: 'unselectedState' };
  }, [active, disabled, inactiveIcon, version]);

  if (version === 'phoenix') {
    return (
      <Button
        version={version}
        disabled={disabled || currentState.value === 'disabledActiveState'}
        onClick={onToggle}
        title={title}
        ref={ref}
        data-component={componentName}
        className={classes([
          props.className,
          styles[`roundedToggleButton-${version}`],
          styles[size],
          styles[currentState.value],
          styles[colorMode],
          {
            [styles.onlyIcon]: onlyIcon,
            [styles.hasIcon]: withIcon && !onlyIcon,
            [styles[`onlyIcon-${size}`]]: onlyIcon,
            [styles.withActiveHoverText]: active && activeHoverText,
            [name]: props.name,
          },
        ])}
      >
        {onlyIcon ? (
          currentState.icon
        ) : (
          <>
            <div className={styles.buttonText}>
              <span className={styles.activeText}>
                {withIcon && currentState.icon} {activeText}
              </span>
              <span className={styles.inactiveText}>
                {withIcon && currentState.icon} {inactiveText}
              </span>
              {withIcon && currentState.icon}
              <span className={styles.displayText}>
                {active ? activeText : inactiveText}
              </span>
            </div>
            <span className={styles.activeHoverText}>{activeHoverText}</span>
          </>
        )}
      </Button>
    );
  }

  // TODO: remove when we stop supporting sundae, this is deprecated
  return (
    <Button
      {...(props as ButtonProps)}
      onClick={onToggle}
      className={classes(
        [
          styles.RoundedToggleButton,
          styles.baseBackground,
          {
            [styles.active]: active,
            [styles.disabled]: disabled,
            [styles.small]: size === 'small',
            [styles.dark]: dark,
            [styles.onlyIcon]: onlyIcon,
            [styles.withActiveHoverText]: active && activeHoverText,
          },
        ],
        props
      )}
      ref={ref}
      data-component={componentName}
      unstyled
      {...buttonProps}
      title={title}
    >
      {onlyIcon ? (
        currentState.icon
      ) : (
        <>
          <div className={styles.buttonText}>
            <span className={styles.activeText}>
              {withIcon && currentState.icon} {activeText}
            </span>
            <span className={styles.inactiveText}>
              {withIcon && currentState.icon} {inactiveText}
            </span>
            {withIcon && currentState.icon} {active ? activeText : inactiveText}
          </div>
          <span className={styles.activeHoverText}>{activeHoverText}</span>
        </>
      )}
    </Button>
  );
});

export { RoundedToggleButton };
